import { useEffect, useRef } from "react";
import type { CalendarEvent } from "../../shared/types.js";
import { CALENDARS_BY_ID } from "../../shared/calendars.js";
import { dayLabel, dayKey, timeRange } from "../lib/eventTime.js";
import { googleCalendarUrl, singleEventIcsUrl } from "../lib/addToCalendar.js";
import { formatDescription } from "../lib/formatDescription.js";

type Props = { event: CalendarEvent; onClose: () => void };

export function EventModal({ event, onClose }: Props) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const source = CALENDARS_BY_ID.get(event.sourceId);
  const color = source?.color ?? "#666";

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    dialogRef.current?.focus();
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  return (
    <div
      className="modal-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        className="modal-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        tabIndex={-1}
        style={{ "--source-color": color } as React.CSSProperties}
      >
        <button type="button" className="modal-close" onClick={onClose} aria-label="Close">
          ×
        </button>
        <p className="modal-day">{dayLabel(dayKey(event))} · {timeRange(event)}</p>
        <h2 id="modal-title" className="modal-title">{event.title || "(no title)"}</h2>
        <div className="modal-meta">
          <span className="event-card-badge" style={{ backgroundColor: color }}>
            {event.sourceName}
          </span>
          {event.location && (
            <a
              className="modal-location"
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.location)}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              📍 {event.location}
            </a>
          )}
        </div>
        {event.description && (
          <div className="event-card-description modal-description">
            {formatDescription(event.description)}
          </div>
        )}
        <div className="event-card-actions">
          <a href={googleCalendarUrl(event)} target="_blank" rel="noopener noreferrer">
            + Google Calendar
          </a>
          <a href={singleEventIcsUrl(event)} download={`${event.id}.ics`}>
            + Apple / Outlook (.ics)
          </a>
          {event.htmlLink && (
            <a href={event.htmlLink} target="_blank" rel="noopener noreferrer">
              View original
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
